import {
  Box,
  Button,
  InputBase,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { reorder } from "../../helpers/list";
import useConfirm from "../../hooks/useConfirm";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown, { DropDownItem } from "./Dropdown";
import { User } from "../../services/swagger";
import IconAvatar from "../atoms/IconAvatar";
import LoadingTextLine from "../atoms/LoadingTextLine";
import React from "react";

interface ItemListProps {
  reorderable?: boolean;
  onChange?: (list: string[]) => void;
  onAdd?: (addedItem: string) => void;
  onSelect?: (selectedItem: string) => void;
  onDelete?: (deletedItem: string) => void;
  renderItem?: (value: string) => ReactNode;
  addList?: DropDownItem[];
  confirmDeletion?: boolean;
  confirmAddition?: boolean;
  items: string[];
  userList?: User[];
  labelFontSize?: number;
  label?: string;
  emptyLabel?: string;
  disabled?: boolean;
  dragDisabled?: boolean;
  unique?: boolean;
  loading?: boolean;
  labelIcon?: React.ElementType;
}

export default function ItemList(props: ItemListProps) {
  const { t } = useTranslation("ItemList");
  const confirm = useConfirm();
  const [newValue, setNewValue] = useState("");
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const popoverOpen = Boolean(popoverAnchorEl);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const newItems = reorder(
      props.items,
      result.source.index,
      result.destination.index
    );

    props.onChange?.(newItems);
  };

  const handleAdd = (value: string) => {
    if (!value) return;
    handlePopoverClose();
    const confirmAdd = () => {
      if (props.unique && props.items.includes(value)) return;
      const newList = [...props.items, value];
      props.onChange?.(newList);
      props.onAdd?.(value);
      setNewValue("");
    };
    if (props.confirmAddition) {
      confirm(confirmAdd);
    } else confirmAdd();
  };

  const handleDelete = (index: number) => {
    const confirmDelete = () => {
      const newArr = [...props.items];
      const item = newArr[index];
      newArr.splice(index, 1);
      props.onChange?.(newArr);
      props.onDelete?.(item);
    };
    if (props.confirmDeletion) confirm(confirmDelete);
    else confirmDelete();
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
  };

  return (
    <Stack spacing={1}>
      <Box>
        <Button
          onClick={handlePopoverOpen}
          variant="text"
          sx={{
            textTransform: "none",
            position: "relative",
            p: 1,
            boxSizing: "content-box",
            m: "-8px !important",
          }}
          fullWidth
          disabled={props.disabled || props.loading}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Stack direction={"row"} spacing={1}>
              {props.labelIcon && (
                <props.labelIcon sx={{ color: "primary.main" }} />
              )}
              <Typography fontWeight={400} fontSize={14} color="primary">
                {props.label || ""}
              </Typography>
            </Stack>

            {!props.disabled && (
              <AddIcon fontSize={"small"} sx={{ color: "grey" }} />
            )}
          </Stack>
        </Button>
      </Box>
      <Box mb={1}></Box>
      {!props.disabled && (
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          sx={{
            "& .MuiPopover-paper": {
              width: popoverAnchorEl ? popoverAnchorEl.clientWidth : "auto", // Matches trigger width
              p: 0.5,
              mt: 1,
            },
          }}
        >
          {props.addList ? (
            <Dropdown
              disabled={props.disabled}
              items={
                props.unique
                  ? props.addList.filter(
                      (e) => !props.items.some((i) => i === e.value)
                    )
                  : props.addList
              }
              className="item-list"
              sx={{
                flexGrow: 1,
                ".MuiInputBase-root": {
                  flexGrow: 1,
                  p: 1,
                  bgcolor: "transparent",
                },
              }}
              onChange={(e, item?: DropDownItem) => {
                const selected = (item?.value as string) || "";
                setNewValue(selected);
                handleAdd(selected); // Pass the selected value directly
              }}
              value={props.addList?.find((e) => e.value === newValue) || ""}
              placeholder={t("NewPlaceholder")}
            />
          ) : (
            <Stack direction="row" alignItems="center">
              <InputBase
                disabled={props.disabled}
                placeholder={t("NewPlaceholder")}
                size="small"
                sx={{ p: 1, flexGrow: 1 }}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
              {!props.disabled && (
                <AddIcon
                  onClick={() => handleAdd(newValue)}
                  color={newValue ? "primary" : "disabled"}
                  sx={{ cursor: "pointer", mr: 0.5 }}
                />
              )}
            </Stack>
          )}
        </Popover>
      )}
      {props.items.length === 0 && props.emptyLabel && (
        <Typography fontSize={11} fontWeight={400} color="textSecondary">
          {props.emptyLabel}
        </Typography>
      )}
      {props.loading ? (
        <LoadingTextLine />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="droppable-list"
            isDropDisabled={Boolean(props.dragDisabled)}
            direction="vertical"
            ignoreContainerClipping
            isCombineEnabled={false}
          >
            {(provided) => (
              <Stack
                spacing={1}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {props.items.map((item: string, index: number) => (
                  <Draggable
                    draggableId={item}
                    index={index}
                    key={item}
                    isDragDisabled={props.dragDisabled || !props.reorderable}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={
                          props.reorderable
                            ? snapshot.isDragging
                              ? { bgcolor: "background.paper" }
                              : {
                                  ":hover": {
                                    bgcolor: "background.paper",
                                  },
                                }
                            : undefined
                        }
                      >
                        <Stack direction={"row"} spacing={"auto"}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {props.reorderable && (
                              <DragHandleIcon color="disabled" />
                            )}
                            <Typography>
                              {props.renderItem
                                ? props.renderItem?.(item)
                                : item}
                            </Typography>
                          </Stack>
                          {!props.disabled && (
                            <IconAvatar
                              icon={CloseIcon}
                              size={18}
                              onClick={() => handleDelete(index) as any}
                            />
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Stack>
  );
}
